import {
  Alert,
  Box,
  Button,
  Card,
  Link,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useId } from 'react';
import { StyledCardContent } from '../../components/containers/StyledCardContent';
import { GPISSectionBody } from '../../components/gpis/GPISSectionBody';
import { GuestGuide, GuestGuideArgs, GuideArticle } from '../../models/GuestGuide';
import { useGetGuestGuideQuery } from '../../redux/rtkQuery/apiSlice';
import { endpoint_getGuestGuide } from '../../utils/mrr/cloudEndpointNames';
import { StandardRTKLoader } from '../loader/StandardRTKLoader';

type GuestGuideProps = GuestGuideArgs & { parkingPhotoId?: string; propertyName?: string };

export function GuestGuideSection(props: GuestGuideProps) {
  const { reservationName, listingId } = props;
  // A different context must not retain the previous property's guide or query data.
  return (
    <GuestGuideRequest
      key={reservationName ? `reservation:${reservationName}` : `listing:${listingId}`}
      {...props}
    />
  );
}

function GuestGuideRequest({ reservationName, listingId, ...photoProps }: GuestGuideProps) {
  const args: GuestGuideArgs =
    reservationName !== undefined ? { reservationName } : { listingId: listingId! };
  const { currentData, isError, isFetching, error, refetch } = useGetGuestGuideQuery(args);

  if (isError || !currentData) {
    return (
      <Stack spacing={1}>
        <StandardRTKLoader
          endpoint={endpoint_getGuestGuide}
          mutationCall={false}
          isError={isError}
          isFetching={isFetching}
          error={error}
          loadedObjectName="property information"
        />
        {isError && (
          <Button onClick={() => refetch()} disabled={isFetching}>
            Retry
          </Button>
        )}
      </Stack>
    );
  }

  if (!currentData.available) {
    return (
      <Alert severity="info">Property information becomes available one day before check-in.</Alert>
    );
  }

  return <GuestGuideContent guide={currentData} {...photoProps} />;
}

function GuestGuideContent({
  guide,
  parkingPhotoId,
  propertyName,
}: {
  guide: GuestGuide;
  parkingPhotoId?: string;
  propertyName?: string;
}) {
  const guideId = useId();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const taxLicense = guide.property_facts.ge_ta_license?.trim();
  const sections = guide.guide.sections.filter(
    (section) =>
      section.articles.length || section.subsections.some((group) => group.articles.length)
  );
  const hasArticles = sections.length > 0;

  return (
    <Stack spacing={1}>
      {!hasArticles && !taxLicense && (
        <Alert severity="info">Property information is not available yet.</Alert>
      )}
      {hasArticles && (
        <Box
          component="section"
          aria-labelledby={`${guideId}-title`}
          sx={{
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            p: { xs: 2.5, md: 4 },
          }}
        >
          <Typography
            id={`${guideId}-title`}
            component="h2"
            variant="h4"
            sx={{ mb: 3, overflowWrap: 'anywhere' }}
          >
            {propertyName ? `${propertyName} Guide` : 'Property Guide'}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: '176px minmax(0, 1fr)' },
              gap: { xs: 3, lg: 5 },
            }}
          >
            <Box
              component="nav"
              aria-label="Guide sections"
              sx={{
                alignSelf: 'start',
                position: { lg: 'sticky' },
                top: 112,
                maxHeight: { lg: 'calc(100vh - 136px)' },
                overflowY: { lg: 'auto' },
              }}
            >
              <Stack
                component="ul"
                direction={{ xs: 'row', lg: 'column' }}
                sx={{ flexWrap: 'wrap', listStyle: 'none', p: 0, m: 0, gap: 0.5 }}
              >
                {sections.map((section, index) => (
                  <Box component="li" key={section.name}>
                    <Link
                      href={`#${guideId}-section-${index}`}
                      underline="hover"
                      onClick={(event) => {
                        event.preventDefault();
                        const heading = document.getElementById(`${guideId}-section-${index}`);
                        heading?.focus({ preventScroll: true });
                        heading?.scrollIntoView({ block: 'start' });
                      }}
                      sx={{
                        display: 'block',
                        py: 1.25,
                        px: 1.5,
                        typography: 'body2',
                        fontWeight: 'fontWeightMedium',
                        overflowWrap: 'anywhere',
                        borderRadius: 1,
                        bgcolor: { xs: 'action.hover', lg: 'transparent' },
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      {section.name}
                    </Link>
                  </Box>
                ))}
              </Stack>
            </Box>
            <Stack spacing={4} sx={{ minWidth: 0, maxWidth: '72ch' }}>
              {sections.map((section, sectionIndex) => (
                <Box
                  component="section"
                  aria-labelledby={`${guideId}-section-${sectionIndex}`}
                  key={section.name}
                  sx={{
                    minWidth: 0,
                    '& + section': { borderTop: 1, borderColor: 'divider', pt: 4 },
                  }}
                >
                  <Typography
                    id={`${guideId}-section-${sectionIndex}`}
                    component="h3"
                    variant="h4"
                    tabIndex={-1}
                    sx={{ mb: 3, scrollMarginTop: '112px', overflowWrap: 'anywhere' }}
                  >
                    {section.name}
                  </Typography>
                  <Stack spacing={3}>
                    {section.articles.map((article) => (
                      <GuideArticleContent key={article.articleNumber} article={article} />
                    ))}
                    {section.subsections
                      .filter((group) => group.articles.length > 0)
                      .map((group) => (
                        <Stack key={group.name} spacing={2.5}>
                          {group.articles.length > 1 && (
                            <Typography
                              component="h4"
                              variant="h6"
                              sx={{ overflowWrap: 'anywhere' }}
                            >
                              {group.name}
                            </Typography>
                          )}
                          {group.articles.map((article) => (
                            <GuideArticleContent
                              key={article.articleNumber}
                              article={article}
                              headingLevel={group.articles.length > 1 ? 'h5' : 'h4'}
                            />
                          ))}
                        </Stack>
                      ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>
        </Box>
      )}
      {parkingPhotoId && (
        <Card variant="outlined" sx={{ boxShadow: 'none' }}>
          <StyledCardContent>
            <Stack spacing={1} alignItems="center" sx={{ width: '100%' }}>
              <Typography variant="h4" sx={{ width: '100%', textAlign: 'left' }}>
                {propertyName ? `${propertyName} Parking Area` : 'Parking Area'}
              </Typography>
              <img
                alt="Parking area"
                src={`https://dreamresorts.tiny.pictures/platform-images/${parkingPhotoId}?width=${
                  isMobile ? 768 : 1920
                }`}
                style={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: 12,
                  maxHeight: '80vh',
                  objectFit: 'contain',
                }}
              />
            </Stack>
          </StyledCardContent>
        </Card>
      )}
      {taxLicense && (
        <Box component="footer" sx={{ px: { xs: 2.5, md: 4 }, py: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
            Tax License: {taxLicense}
          </Typography>
        </Box>
      )}
    </Stack>
  );
}

function GuideArticleContent({
  article,
  headingLevel = 'h4',
}: {
  article: GuideArticle;
  headingLevel?: 'h4' | 'h5';
}) {
  const titleId = useId();

  return (
    <Box component="article" aria-labelledby={titleId} sx={{ minWidth: 0 }}>
      <Typography
        id={titleId}
        component={headingLevel}
        variant="subtitle1"
        sx={{ mb: 1, overflowWrap: 'anywhere' }}
      >
        {article.title}
      </Typography>
      <Box
        sx={{
          overflowWrap: 'anywhere',
          '& > div': { lineHeight: 1.75 },
          '& p': { mt: 0, mb: 2 },
          '& img, & video': { maxWidth: '100%', height: 'auto', alignSelf: 'start' },
          '& iframe': { maxWidth: '100%' },
          '& table': { display: 'block', maxWidth: '100%', overflowX: 'auto' },
          '& pre': { maxWidth: '100%', overflowX: 'auto' },
          '& th, & td': {
            minWidth: 128,
            px: 1.5,
            py: 1,
            textAlign: 'left',
            verticalAlign: 'top',
            borderBottom: 1,
            borderColor: 'divider',
          },
          '& a': { color: 'primary.main', overflowWrap: 'anywhere' },
          '& :last-child': { mb: 0 },
        }}
      >
        <GPISSectionBody body={article.body} />
      </Box>
    </Box>
  );
}
